import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { Box, Snackbar, Alert, FormControlLabel, Checkbox, FormControl } from "@mui/material";
import GlossaryDialog from '../components/usage/GlossaryDialog';
import SettingsDialog from '../components/usage/SettingsDialog';
import UsageTable from '../components/usage/UsageTable';
import { UsageApi } from '../utils/api';
import { AppContext } from '../store/AppContext';


const Usage = () => {
    const [openGlossary, setOpenGlossary] = useState(false);
    const [openSettings, setOpenSettings] = useState(false);
    const [users, setUsers] = useState([]);
    const [emailOnNewReport, setEmailOnNewReport] = useState(false);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [usageData, setUsageData] = useState([]);
    const [selectedMonthYear, setSelectedMonthYear] = useState(() => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
    });
    
    // Keep track of users state before opening dialog
    const [usersBeforeDialog, setUsersBeforeDialog] = useState([]);
    const initialized = useRef(false);
    const isMounted = useRef(true);

    const { state } = useContext(AppContext);
    const { reportingCohortId } = state;
    const isPrimary = state.currentCohort?.role === 'primary';
    const [reportName, setReportName] = useState('');

    const lastFetchRef = useRef({ reportingCohortId: null, selectedMonthYear: null });

    const [glossaryItems, setGlossaryItems] = useState([]);

    // Memoize fetchUsageData to prevent recreation on every render
    const fetchUsageData = useCallback(async (monthYear = selectedMonthYear) => {
        console.log('fetchUsageData called with monthYear:', monthYear);
        if (!reportingCohortId) return;
        
        setLoading(true);
        try {
            const response = await UsageApi.getReportData(reportingCohortId, monthYear);
            setUsageData(response.data || []);
        } catch (error) {
            console.error('Error fetching usage data:', error);
            setSnackbar({
                open: true,
                message: 'Failed to load usage data. Please try again.',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    }, [reportingCohortId, selectedMonthYear]);

    // Single effect for initialization
    useEffect(() => {
        if (!reportingCohortId || !selectedMonthYear) return;

        // Only fetch if either value has changed
        if (
            lastFetchRef.current.reportingCohortId === reportingCohortId &&
            lastFetchRef.current.selectedMonthYear === selectedMonthYear
        ) {
            return;
        }

        lastFetchRef.current = { reportingCohortId, selectedMonthYear };

        const fetchData = async () => {
            setLoading(true);
            try {
                const promises = [UsageApi.getReportData(reportingCohortId, selectedMonthYear)];
                if (isPrimary) {
                    promises.push(UsageApi.getCohortReportConfig(reportingCohortId));
                }
                const [usageResponse, configResponse] = await Promise.all(promises);
                setUsageData(usageResponse.data || []);
                if (isPrimary && configResponse) {
                    setReportName(configResponse?.data?.name || '');
                    setUsers(configResponse?.data?.users || []);
                    setEmailOnNewReport(configResponse?.data?.settings?.emailOnNewReport || false);
                }
            } catch (error) {
                console.error('Error initializing data:', error);
                setSnackbar({
                    open: true,
                    message: 'Failed to load data. Please try again.',
                    severity: 'error'
                });
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [reportingCohortId, isPrimary, selectedMonthYear]);

    // Handle month/year change
    const handleMonthYearChange = (monthYear) => {
        setSelectedMonthYear(monthYear);
    };

    const handleOpenGlossary = (items) => {
        setGlossaryItems(items);
        setOpenGlossary(true);
    };

    const handleCloseGlossary = () => setOpenGlossary(false);
    
    const handleOpenSettings = () => {
        // Store current users state before opening dialog
        setUsersBeforeDialog([...users]);
        setOpenSettings(true);
    };

    const handleCloseSettings = (shouldRevert = false) => {
        if (shouldRevert) {
            // Revert to the state before dialog was opened
            setUsers([...usersBeforeDialog]);
        }
        setOpenSettings(false);
    };

    const handleSaveSettings = async (name, updatedUsers, newEmailOnNewReport) => {
        try {
            // Clean the users data before sending to API
            const cleanedUsers = updatedUsers.map(user => {
                const { isNew, ...cleanUser } = user;
                return cleanUser;
            });

            // Format the data according to the API requirements
            const reportData = {
                name,
                settings: {
                    emailOnNewReport: newEmailOnNewReport
                },
                users: cleanedUsers
            };

            // Send to API
            await UsageApi.updateCohortReportConfig(reportingCohortId, reportData);

            // Update local state
            setUsers(cleanedUsers);
            setEmailOnNewReport(newEmailOnNewReport);
            handleCloseSettings(false);
            
            // Show success message
            setSnackbar({
                open: true,
                message: 'Report settings updated successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error updating report settings:', error);
            
            // Show error message
            setSnackbar({
                open: true,
                message: error.message || 'Failed to update report settings',
                severity: 'error'
            });
            
            // Don't close the dialog on error
            return;
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    return (
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100vh' }}>
            <UsageTable 
                onOpenGlossary={handleOpenGlossary}
                onOpenSettings={handleOpenSettings}
                usageData={usageData}
                loading={loading}
                onMonthYearChange={handleMonthYearChange}
            />

            <GlossaryDialog 
                open={openGlossary}
                onClose={handleCloseGlossary}
                glossaryItems={glossaryItems}
            />

            <SettingsDialog 
                open={openSettings}
                onClose={handleCloseSettings}
                name={reportName}
                users={users}
                onSave={handleSaveSettings}
                emailOnNewReport={emailOnNewReport}
            >
                <FormControlLabel
                    control={<Checkbox name="emailNotifications" />}
                    label="Email users when new report is available"
                />
            </SettingsDialog>

            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Usage;
